import type { FormSubmissionStatus } from '../utilities/form-status'
import { DatePipe, NgIf, NgTemplateOutlet } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    output,
    SimpleChanges,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { Venue } from '../domain/Venue'
import { EnquiryService } from '../services/enquiry.service'
import { CreateEnquiryRequestDTO } from '../services/DTOs/CreateEnquiryRequestDTO'
import { markAllFormControlsAsTouched } from '../utilities/form-touched'
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import {
    CountryISO,
    NgxIntlTelInputModule,
    PhoneNumberFormat,
    SearchCountryField,
} from 'ngx-intl-tel-input-gg'

@Component({
    selector: 'app-enquiry-form',
    standalone: true,
    imports: [DatePipe, NgIf, NgTemplateOutlet, NgxIntlTelInputModule, ReactiveFormsModule],
    templateUrl: './enquiry-form.component.html',
    styleUrls: ['./enquiry-form.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnquiryFormComponent implements OnInit, OnChanges {
    @Input() venue!: Venue
    @Input() partySize!: number
    @Input() selectedDate!: NgbDateStruct
    @Input() selectedTime!: Date
    @Input() showBackButton = false
    @Output() backRequested = new EventEmitter<void>()
    @Output() enquirySubmitted = new EventEmitter<void>()

    submissionStatusChange = output<FormSubmissionStatus>()

    form!: FormGroup
    isSubmitting = false
    isSubmitted = false
    errorMessage: string | null = null
    phoneNumberCountryCode!: CountryISO

    protected readonly CountryISO = CountryISO
    protected readonly SearchCountryField = SearchCountryField
    protected readonly PhoneNumberFormat = PhoneNumberFormat

    constructor(
        private fb: FormBuilder,
        private enquiryService: EnquiryService,
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            emailAddress: ['', [Validators.required, Validators.email]],
            phoneNumber: ['', Validators.required],
            partySize: [this.partySize, [Validators.required, Validators.min(1)]],
            message: [''],
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['venue']) {
            const countryCode = this.venue.address.getISO3166CountryCode().toLowerCase()
            this.phoneNumberCountryCode = countryCode === 'uk'
                ? CountryISO.UnitedKingdom
                : countryCode as CountryISO
        }
    }

    onSubmit() {
        markAllFormControlsAsTouched(this.form)
        if (this.form.invalid) {
            this.submissionStatusChange.emit('validation_failed')
            return
        }

        this.submissionStatusChange.emit('started')
        this.isSubmitting = true
        this.errorMessage = null

        const date = this.formatDate(this.selectedDate)
        const time = this.formatTime(this.selectedTime)

        const phoneNumberValue = this.form.get('phoneNumber')?.value
        const phoneNumber = phoneNumberValue?.e164Number || phoneNumberValue

        const enquiry = new CreateEnquiryRequestDTO(
            this.form.get('firstName')?.value,
            this.form.get('lastName')?.value,
            this.form.get('emailAddress')?.value,
            phoneNumber,
            this.form.get('partySize')?.value,
            date,
            time,
            this.form.get('message')?.value || null
        )

        this.enquiryService.submitEnquiry(
            this.venue.organisationId,
            this.venue.businessId,
            this.venue.id,
            enquiry
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.isSubmitting = false
                    this.isSubmitted = true
                    this.submissionStatusChange.emit('succeeded')
                    this.enquirySubmitted.emit()
                    this.changeDetectorRef.markForCheck()
                },
                error: (error) => {
                    this.submissionStatusChange.emit('failed')
                    this.isSubmitting = false
                    this.errorMessage = 'Sorry, there was a problem submitting your enquiry. Please try again or contact us directly.'
                    console.error('Enquiry submission error:', error)
                    this.changeDetectorRef.markForCheck()
                },
            })
    }

    protected get selectedDateAsDate(): Date {
        return new Date(
            this.selectedDate.year,
            this.selectedDate.month - 1,
            this.selectedDate.day
        )
    }

    private formatDate(ngbDate: NgbDateStruct): string {
        const year = ngbDate.year
        const month = String(ngbDate.month).padStart(2, '0')
        const day = String(ngbDate.day).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    private formatTime(date: Date): string {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
    }
}
